import { createColumnHelper } from '@tanstack/react-table';
import cn from 'classnames';
import { useMemo } from 'react';
import { Link } from '#components/atoms/link/Link.tsx';
import { Table } from '#components/patterns/table/Table.tsx';
import type { RoleListItem } from '#dn-types/role.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { RoleActionMenu } from './role-action-menu/RoleActionMenu.tsx';
import styles from './RolesList.module.css';

type Props = {
  data: RoleListItem[];
  isFetching: boolean;
};

const columnHelper = createColumnHelper<RoleListItem>();

export function RolesList({ data, isFetching }: Props) {
  const location = useAppLocation();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell(info) {
          const name = info.getValue();
          const { id } = info.row.original;
          return (
            <Link className="a inline truncate" to={`/roles/${id}`} state={{ from: location }}>
              {name}
            </Link>
          );
        },
        meta: {
          truncate: true,
        },
      }),
      columnHelper.accessor('hostCount', {
        header: 'Hosts',
        size: 30,
        cell(info) {
          const count = info.getValue();
          if (!count) return count;
          const { id } = info.row.original;
          return (
            <Link className="a" to={`/hosts?filter.roleID=${id}`}>
              {count}
            </Link>
          );
        },
        meta: { align: 'center', className: styles.RolesList_hosts },
      }),
      columnHelper.accessor('firewallRulesCount', {
        header: 'Rules',
        size: 60,
        meta: { align: 'center' },
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        size: 180,
        meta: {
          truncate: true,
          className: styles.RolesList_description,
        },
      }),
      columnHelper.display({
        id: 'action-menu',
        header: '',
        size: 34,
        cell(info) {
          const { id } = info.row.original;
          return (
            <div className="flex justify-end">
              <RoleActionMenu value={id} />
            </div>
          );
        },
      }),
    ],
    [location]
  );

  return (
    <div className={cn({ 'opacity-dim': isFetching })}>
      <Table data={data} columns={columns} />
    </div>
  );
}
